import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';

export function NewBodyRentalRequestModal({ onClose, clientId }: { onClose: () => void; clientId?: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    clientId: clientId ?? '',
    projectId: '',
    title: '',
    description: '',
    neededRoles: 1,
  });

  const { data: clients } = useQuery({
    queryKey: ['clients-lite'],
    queryFn: () => api.get<{ items: Array<{ id: string; legalName: string }> }>('/clients?pageSize=200'),
  });
  const { data: projects } = useQuery({
    queryKey: ['projects-lite', form.clientId],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string; code: string }> }>(`/projects?pageSize=200${form.clientId ? `&clientId=${form.clientId}` : ''}`),
    enabled: !!form.clientId,
  });

  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/body-rental', {
      clientId: form.clientId,
      projectId: form.projectId || undefined,
      title: form.title,
      description: form.description || undefined,
      neededRoles: form.neededRoles,
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['body-rental'] });
      toast.ok('Richiesta creata', form.title);
      onClose();
      navigate(`/talent/body-rental/${res.id}`);
    },
    onError: (err) => toast.alert('Errore creazione richiesta', err instanceof ApiError ? err.message : 'Riprova'),
  });

  return (
    <Modal
      title="Nuova richiesta Body Rental"
      kicker="Talent · Somministrazione"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()}
            disabled={!form.clientId || !form.title || create.isPending}>
            Crea richiesta <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-section">
        <div className="form-section-hd">Richiesta del cliente</div>
        <div className="form-grid">
          <Field label="Cliente" required span={12}>
            <select className="inp" value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value, projectId: '' })}>
              <option value="">— Seleziona cliente —</option>
              {(clients?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.legalName}</option>)}
            </select>
          </Field>
          <Field label="Titolo richiesta" required hint="Es. 2 sviluppatori React per progetto X" span={12}>
            <input className="inp" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Progetto collegato" hint="Opzionale" span={8}>
            <select className="inp" value={form.projectId} disabled={!form.clientId} onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
              <option value="">— Nessuno —</option>
              {(projects?.items ?? []).map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
            </select>
          </Field>
          <Field label="Figure richieste" span={4}>
            <input className="inp mono text-right" type="number" min={1} value={form.neededRoles} onChange={(e) => setForm({ ...form, neededRoles: Math.max(1, Number(e.target.value)) })} />
          </Field>
          <Field label="Note / descrizione" span={12}>
            <textarea className="inp" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Competenze richieste, durata, sede…" />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
